import { Grid, Typography } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import useIsMounted from '../../../hooks/useIsMounted'
import LeftHalfLayout from '../../../layouts/buy/LeftHalfLayout'
import { CartDispensaryPromoCode } from '../../../lib/cart/dispensary-promo'
import { usePromoCodeUtilities } from '../../../lib/cart/hooks/usePromoCodeUtilities'
import { logAndCaptureException } from '../../../utils/errorTools'

const Amount = styled.span`
  font-family: ${({ theme }) => theme.fontFamilies.gotham};
  font-size: 15px;
  line-height: 30px;
  color: ${({ theme }) => theme.colors.blackPearlBlue.shade700};
  font-weight: 500;
`

export interface PromoTotalSummarySectionProps {
  amount: string | number
  cartDispensaryPromoCodes: CartDispensaryPromoCode[] | undefined
  className?: string
  coupons: CartFragment['applied_coupons']
}

const PromoTotalSummarySection: React.FC<PromoTotalSummarySectionProps> = ({
  amount,
  cartDispensaryPromoCodes,
  className,
  coupons,
}) => {
  const { concealCode, loading: utilitiesLoading } = usePromoCodeUtilities({
    cartDispensaryPromoCodes,
  })
  const [commaSeparatedCoupons, setCommaSeparatedCoupons] = useState<string>('')
  const isMounted = useIsMounted()

  useEffect(() => {
    if (!coupons || utilitiesLoading) {
      setCommaSeparatedCoupons('')
      return
    }
    coupons
      .reduce(async (acc: Promise<string>, coupon) => {
        if (!coupon) return acc
        const awaitedAcc = await acc
        return `${awaitedAcc}${awaitedAcc.length > 0 ? ', ' : ''}${await concealCode(coupon.code)}`
      }, Promise.resolve(''))
      .then((value) => {
        if (isMounted.current) {
          setCommaSeparatedCoupons(value)
        }
      })
      .catch(logAndCaptureException)
  }, [coupons, concealCode, isMounted, utilitiesLoading])

  if (!commaSeparatedCoupons || !amount) {
    return null
  }

  return (
    <Grid className={className} container item justify="space-between" wrap="nowrap" xs={12}>
      <Grid item zeroMinWidth>
        <Typography variant="body2" gutterBottom noWrap>
          Promo: <strong>{commaSeparatedCoupons}</strong>
        </Typography>
      </Grid>
      <Grid item style={{ marginLeft: 16 }}>
        <Amount>-{amount}</Amount>
      </Grid>
    </Grid>
  )
}

const StyledPromoTotalSummarySection = styled(PromoTotalSummarySection)`
  ${LeftHalfLayout} & {
    padding-left: 15px;
    padding-right: 15px;
  }
`

export default StyledPromoTotalSummarySection
